import { PATH } from '@repo/constants';
import { DashboardLayout, MainSuspense, RouterSuspense } from '@ui/components';
import { lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';

const AuthorizationPage = lazy(() => import('@ui/pages/authorization'));
const ErrorPage = lazy(() => import('@ui/pages/error'));
const DashboardPage = lazy(() => import('../pages/dashboard'));
const ActivityLogsPage = lazy(() => import('../pages/tools/activity-logs'));
const FailedFixMessagesPage = lazy(
  () => import('../pages/fix-messages/failed-fix-messages'),
);
const InsertFixMessagesPage = lazy(
  () => import('../pages/fix-messages/insert-fix-messages'),
);
const SFTPPage = lazy(() => import('../pages/file-management/sftp'));
const SFTPDetailsPage = lazy(
  () => import('../pages/file-management/sftp-details'),
);
const ParameterPage = lazy(() => import('../pages/configuration/parameter'));
const ParameterValuePage = lazy(
  () => import('../pages/configuration/parameter-value'),
);
const ParameterValueIdPage = lazy(
  () => import('../pages/configuration/parameter-value-id'),
);
const SettlementCyclesPage = lazy(
  () => import('../pages/configuration/settlement-cycles'),
);
const FilesMappingPage = lazy(
  () => import('../pages/file-management/files-mapping'),
);

const UsersPage = lazy(() => import('../pages/user-management/users'));
const UserDetailsPage = lazy(
  () => import('../pages/user-management/user-details'),
);

const GroupsPage = lazy(() => import('../pages/user-management/groups'));
const GroupDetailsPage = lazy(
  () => import('../pages/user-management/group-details'),
);

const PermissionsPage = lazy(
  () => import('../pages/user-management/permissions'),
);
const PermissionDetailsPage = lazy(
  () => import('../pages/user-management/permission-details'),
);
const ConstantsPage = lazy(() => import('../pages/system/constants'));

const MaintenancePage = lazy(() => import('../pages/system/maintenance'));

const MaintenancePreviewPage = lazy(() => import('@ui/pages/maintenance'));

const SFTPFileDetailsPage = lazy(
  () => import('../pages/file-management/sftp-file-details'),
);

const BackupsPage = lazy(() => import('../pages/tools/backups'));
const FixAggregationPage = lazy(
  () => import('../pages/fix-messages/fix-aggregation'),
);
const PositionsRecalculationPage = lazy(
  () => import('../pages/tools/positions-recalculation'),
);
const DailyConfirmationConfigPage = lazy(
  () => import('../pages/tools/daily-confirmation-config'),
);
const DailyConfirmationLogsPage = lazy(
  () => import('../pages/tools/daily-confirmation-logs'),
);
const CustodyDataPage = lazy(() => import('../pages/custody-data'));
const CustodyDataDetailsPage = lazy(
  () => import('../pages/custody-data/details'),
);
const CustodyDataManualPage = lazy(
  () => import('../pages/custody-data/manual'),
);
const CustodyDataRunPage = lazy(
  () => import('../pages/custody-data/run'),
);
const NotificationsPage = lazy(() => import('../pages/notifications'));
const NotificationDetailsPage = lazy(
  () => import('../pages/notification-details'),
);


export const getRouter = () => {
  return createBrowserRouter([
    {
      path: PATH.DASHBOARD,
      element: (
        <MainSuspense>
          <DashboardLayout />
        </MainSuspense>
      ),
      ErrorBoundary: ErrorPage,
      children: [
        {
          path: '',
          index: true,
          element: (
            <RouterSuspense>
              <DashboardPage />
            </RouterSuspense>
          ),
        },
        {
          path: PATH.TOOLS_ACTIVITY_LOGS,
          element: <ActivityLogsPage />,
        },
        {
          path: PATH.TOOLS_FAILED_FIX_MESSAGES,
          element: <FailedFixMessagesPage />,
        },
        {
          path: PATH.TOOLS_INSERT_FIX_MESSAGES,
          element: <InsertFixMessagesPage />,
        },
        {
          path: PATH.FILE_MANAGEMENT_SFTP,
          element: <SFTPPage />,
        },
        {
          path: PATH.FILE_MANAGEMENT_SFTP + '/:id',
          children: [
            {
              path: '',
              element: <SFTPDetailsPage />,
            },
            {
              path: `${PATH.FILE_MANAGEMENT_SFTP}/:id/file/create`,
              element: <SFTPFileDetailsPage />,
            },
            {
              path: `${PATH.FILE_MANAGEMENT_SFTP}/:id/file/:fileId`,
              element: <SFTPFileDetailsPage />,
            },
          ],
        },
        {
          path: PATH.CONFIGURATION_PARAMETER,
          element: <ParameterPage />,
        },
        {
          path: PATH.CONFIGURATION_PARAMETER_VALUE + '/:id',
          element: <ParameterValuePage />,
        },
        {
          path: PATH.CONFIGURATION_PARAMETER_VALUE_ID,
          element: <ParameterValueIdPage />,
        },
        {
          path: PATH.CONFIGURATION_SETTLEMENT_CYCLES,
          element: <SettlementCyclesPage />,
        },
        {
          path: PATH.FILE_MANAGEMENT_FILES_MAPPING,
          element: <FilesMappingPage />,
        },
        {
          path: PATH.USER_MANAGEMENT_USERS,
          element: <UsersPage />,
        },
        {
          path: PATH.USER_MANAGEMENT_USERS + '/:id',
          element: <UserDetailsPage />,
        },
        {
          path: PATH.USER_MANAGEMENT_GROUPS,
          element: <GroupsPage />,
        },
        {
          path: PATH.USER_MANAGEMENT_GROUPS + '/:id',
          element: <GroupDetailsPage />,
        },
        {
          path: PATH.USER_MANAGEMENT_PERMISSIONS,
          element: <PermissionsPage />,
        },
        {
          path: PATH.USER_MANAGEMENT_PERMISSIONS + '/:id',
          element: <PermissionDetailsPage />,
        },
        {
          path: PATH.SYSTEM_CONSTANTS,
          element: <ConstantsPage />,
        },
        {
          path: PATH.SYSTEM_MAINTENANCE,
          element: <MaintenancePage />,
        },
        {
          path: PATH.TOOLS_BACKUPS,
          element: <BackupsPage />,
        },
        {
          path: PATH.TOOLS_FIX_AGGREGATION,
          element: <FixAggregationPage />,
        },
        {
          path: PATH.TOOLS_POSITIONS_RECALCULATION,
          element: <PositionsRecalculationPage />,
        },
        {
          path: PATH.TOOLS_DAILY_CONFIRMATION_CONFIG,
          element: <DailyConfirmationConfigPage />,
        },
        {
          path: PATH.TOOLS_DAILY_CONFIRMATION_LOGS,
          element: <DailyConfirmationLogsPage />,
        },
        {
          path: PATH.NOTIFICATIONS,
          element: <NotificationsPage />,
        },
        {
          path: PATH.NOTIFICATIONS + '/:id',
          element: <NotificationDetailsPage />,
        },
        {
          path: PATH.CUSTODY_DATA,
          element: (
            <CustodyDataPage />
          ),
        },
        {
          path: PATH.CUSTODY_DATA_CONFIGURATION,
          element: <CustodyDataPage />,
        },
        {
          path: PATH.CUSTODY_DATA_CONFIGURATION + '/:id',
          element: <CustodyDataDetailsPage />,
        },
        {
          path: PATH.CUSTODY_DATA_CONFIGURATION_MANUAL + '/:id',
          element: <CustodyDataManualPage />,
        },
        {
          path: PATH.CUSTODY_DATA_RUN,
          element: <CustodyDataRunPage />,
        },
        {
          path: '*',
          element: <Navigate to={PATH.DASHBOARD} />,
        },
      ],
    },
    {
      path: PATH.AUTH_LOGIN,
      element: (
        <MainSuspense>
          <AuthorizationPage />
        </MainSuspense>
      ),
    },
    {
      path: PATH.MAINTENANCE_PREVIEW + '/:type',
      element: (
        <RouterSuspense>
          <MaintenancePreviewPage isPreview />
        </RouterSuspense>
      ),
    },
    {
      path: '*',
      element: <Navigate to={PATH.AUTH_LOGIN} />,
    },
  ]);
};
